import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { OAuth2App, ShortOAuth2App } from '../../../../store/auth/auth'
import { addToast } from '../../../../store/toast/toastSlice'
import { Col, Container, Row } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import { useDeleteUserIntegrationMutation, useGetMeQuery } from '../../../../store/user/user'

interface RevokeMyOAuth2AppAccessModalProps {
  oAuth2App: OAuth2App | ShortOAuth2App
  show: boolean
  onHide: () => void
}

export const RevokeMyOAuth2AppAccessModal: React.FC<RevokeMyOAuth2AppAccessModalProps> = ({ oAuth2App, show, onHide }) => {
  const { clientId, name } = oAuth2App

  const accountId = useAppSelector(selectAccountId)

  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })

  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()

  const dispatch = useAppDispatch()

  const showRevokeAccessError = () => {
    dispatch(
      addToast({
        variant: 'error',
        messages: ['Failed to revoke access. Please try again.'],
      }),
    )
  }

  const revokeAccess = async () => {
    try {
      if (!userData?.integrations) {
        showRevokeAccessError()
        return
      }
      const integration = userData.integrations.find((i) => i.serviceId === clientId)
      if (!integration) {
        showRevokeAccessError()
        return
      }
      const response = await deleteUserIntegration({
        accountId,
        payload: {
          userId: userData.id,
          integrationId: integration.id,
        },
      })
      if (response.error) {
        showRevokeAccessError()
        return
      }
      dispatch(
        addToast({
          variant: 'success',
          title: 'Success',
          messages: ['Successfully revoked access.'],
        }),
      )
    } catch (e) {
      console.error(e)
      showRevokeAccessError()
    } finally {
      onHide()
    }
  }

  const body = (
    <Container>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>You'll lose access shortly. Any automations or agents using this connection may stop working.</div>
        </Col>
      </Row>
    </Container>
  )
  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<h4 className='p-2'>Revoke access to {name}?</h4>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={revokeAccess}
      footerPrimaryActionLoading={isDeletingIntegration}
      footerPrimaryActionTitle='Revoke access'
      footerPrimaryActionButtonVariant='primary'
    />
  )
}
